import { useEffect, useState } from 'react'

/** Delays a rapidly changing value — one network request per pause, not per keystroke. */
export function useDebouncedValue<T>(value: T, delay = 250): T {
  const [debounced, setDebounced] = useState(value)

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay)
    return () => window.clearTimeout(timer)
  }, [value, delay])

  return debounced
}

/**
 * Copies text and reports success for a couple of seconds.
 *
 * `navigator.clipboard` is unavailable on an insecure origin, so the failure
 * path is real rather than theoretical and the caller is told about it.
 */
export function useCopyToClipboard(resetAfter = 2000) {
  const [copied, setCopied] = useState<string | null>(null)

  useEffect(() => {
    if (!copied) return
    const timer = window.setTimeout(() => setCopied(null), resetAfter)
    return () => window.clearTimeout(timer)
  }, [copied, resetAfter])

  async function copy(text: string, key = text): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(key)
      return true
    } catch {
      return false
    }
  }

  return { copied, copy }
}
